import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Outlet, Link } from "react-router-dom";
import {
  AppBar,
  Box,
  Drawer,
  Toolbar,
  IconButton,
  Container,
  Button,
  ClickAwayListener,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Phone } from "@mui/icons-material";
import styled from "@emotion/styled";
import Footer from "./Footer";
import Profile from "../pages/Profile";
import CallMeDialog from "../pages/CallMeDialog";
import ScriptLoader from "../account/ScriptLoader";
import CallMeDialogLogin from "../pages/CallMeDialogLogin";
import { useNav } from "../../context/NavProvider";
import { useAuth } from "../../context/AuthProvider";
import { useAccount } from "../../context/AccountProvider";
import { useSfs } from "../../context/SfsProvider";
import { useArrangement } from "../../context/ArrangementProvider";
import { useCallMe } from "../../context/CallMeProvider";
import useAppData from "../../hooks/useAppData";
const isExternalUrl = (url) => /^https?:\/\//i.test(url.trim());

function ResponsiveAppBar() {
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [openCallback, setOpenCallback] = useState(false);
  const [openCallDialogLogin, setOpenCallDialogLogin] = useState(false);
  const [callbackSet, setCallbackSet] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const menuTimeoutRef = useRef();

  const { useGetAccount } = useAccount();
  const { account } = useGetAccount();
  const { showFooter, showCallMeIcon, hideHeaderNavBars } = useNav();
  const { accessToken } = useAuth();
  const { clientConfig } = useAppData();
  const { disposableIncome, useGetSfs, monthsSinceReview } = useSfs();
  const { sfs } = useGetSfs();
  const { hasArrangement, lastSavedPlan } = useArrangement();
  const { getCallbacks } = useCallMe();

  const AppBarStyle = styled("div")(({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      color: clientConfig.layout.header.mobileColour,
      backgroundColor: clientConfig.layout.header.mobileBackgroundColour
    },
    [theme.breakpoints.up("md")]: {
      color: clientConfig.layout.header.colour,
      backgroundColor: clientConfig.layout.header.backgroundColour
    }
  }));

  const showAccountMenu = clientConfig?.layout?.navigation?.manageMyAccount?.display !== false;
  const showInfoMenu = clientConfig?.layout?.navigation?.infoAndSupport?.display !== false;

  const getVisibleItems = (items = []) =>
    items.filter(item => item.display !== false).map(item => ({
      name: item.label,
      href: item.url || '#',
    }));

  const baseAccountItems = getVisibleItems(clientConfig?.layout?.navigation?.manageMyAccount?.subCategories);
  const infoItems = getVisibleItems(clientConfig?.layout?.navigation?.infoAndSupport?.subCategories);

  const createAmendPlanLabel = "Create or amend repayment plan";
  const hasValidSfs = sfs && monthsSinceReview <= 6;
  const createAmendPlanHref = (hasValidSfs && disposableIncome > 1) ? "/repayment" : "/sfs";

  const filteredAccountItems = useMemo(() => {
    return baseAccountItems
      .filter(item => {
        const isCreateAmendItem = item.name === createAmendPlanLabel;
        if (!isCreateAmendItem) return true;

      const noPlanYet = !lastSavedPlan;
      const isSettlement = lastSavedPlan?.isSettlement;
      const inPaymentWindow = lastSavedPlan?.inPaymentWindow;
      const noValidSfs = !sfs || monthsSinceReview > 6;
      const noArrangement = !hasArrangement;

      if (inPaymentWindow) return false;
      const shouldShowCreateAmendItem =
        noPlanYet ||
        (!isSettlement && hasArrangement) ||
        noArrangement ||
        noValidSfs;

      return shouldShowCreateAmendItem;
    })
    .map(item => {
      if (item.name === createAmendPlanLabel) {
        return { ...item, href: createAmendPlanHref };
      }
      return item;
    });
}, [baseAccountItems, lastSavedPlan, hasArrangement, sfs, monthsSinceReview, createAmendPlanHref]);


  const menus = {
    account: filteredAccountItems,
    info: infoItems,
  };

  const handleOpenCallback = () => setOpenCallback(true);
  const handleCloseCallback = () => setOpenCallback(false);
  const handleCloseCallbackLogin = () => setOpenCallDialogLogin(false);
  const handleOpenCallbackLogin = () => setOpenCallDialogLogin(true);

  const handleCallbackSet = async (ref, forceSet = false) => {
    if (forceSet) return setCallbackSet(true);
    try {
      const response = await getCallbacks(ref);
      const now = new Date();
      const futureCallbacks = response?.callbacks?.filter(cb => new Date(cb.requestedCallbackDatetime) > now) || [];
      setCallbackSet(futureCallbacks.length > 0);
    } catch {
      setCallbackSet(false);
    }
  };

  useEffect(() => {
    const checkStatus = async () => {
      if (!accessToken || !account?.accountReference) return;
      await handleCallbackSet(account.accountReference);
    };
    checkStatus();
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, [accessToken, account?.accountReference]);

  const handleMouseEnter = useCallback((menuKey) => {
    clearTimeout(menuTimeoutRef.current);
    setOpenMenu(menuKey);
  }, []);

  const handleMouseLeave = useCallback(() => {
    menuTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 200);
  }, []);

  const toggleMenu = (menuKey) => {
    clearTimeout(menuTimeoutRef.current);
    setOpenMenu(prev => (prev === menuKey ? null : menuKey));
  };

  const toggleProfileDrawer = (open) => () => {
    setProfileDrawerOpen(open);
  };

  const closeMenu = () => {
    clearTimeout(menuTimeoutRef.current);
    setOpenMenu(null);
  };

  const handleClick = () => (window.location.href = "/");

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return clientConfig && (
    <>
      <ScriptLoader />
      <AppBar position="static">
        <AppBarStyle>
          <Container maxWidth='xl'>
            <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {!hideHeaderNavBars && <IconButton sx={{ display: { md: 'none' } }} onClick={toggleMobileMenu}>
                  {mobileMenuOpen ? (
                    <CloseIcon sx={{ color: 'primary.contrastText', fontSize: '2rem' }} />
                  ) : (
                      <MenuIcon sx={{ color: 'primary.contrastText', fontSize: '2rem' }} />
                  )}
                </IconButton>}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: { xs: 'relative', sm: 'absolute', md: 'relative' },
                    left: { xs: 'auto', sm: '50%', md: 'auto' },
                    transform: { xs: 'none', sm: 'translateX(-50%)', md: 'none' },
                    pl: { xs: 2, sm: 0 },
                  }}
                >
                  <Link onClick={handleClick}>
                    <Box
                      component="img"
                      src={clientConfig.layout.header.logo}
                      alt="Logo"
                      sx={{ display: { xs: 'none', md: 'block' }, width: 130 }}
                    />
                    <Box
                      component="img"
                      src={clientConfig.layout.header.logoMobile}
                      alt="Logo"
                      sx={{ display: { xs: 'block', md: 'none' }, width: 130 }}
                    />
                  </Link>
                </Box>

                {!hideHeaderNavBars && (
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 5 }}>
                    {[
                      ...(accessToken && showAccountMenu ? ['account'] : []),
                      ...(showInfoMenu ? ['info'] : [])
                    ].map((menuKey, idx) => (
                      <ClickAwayListener onClickAway={closeMenu} key={menuKey}>
                        <Box
                          onMouseEnter={() => handleMouseEnter(menuKey)}
                          onMouseLeave={handleMouseLeave}
                          sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
                        >
                          <Button
                            onClick={() => toggleMenu(menuKey)}
                            sx={{
                              ml: idx > 0 ? 4 : 0,
                              fontWeight: 'bold',
                              textTransform: 'none',
                              color: clientConfig.layout.header.headerButtonColour,
                              '&:hover': {
                                backgroundColor: clientConfig.layout.header.headerButtonHoverColour,
                              },
                              fontSize: '16px',
                              height: '64px',
                              display: 'flex',
                              alignItems: 'center',
                              px: 2,
                            }}
                          >
                            {menuKey === 'account' ? 'Manage my account' : 'Info & Support'}
                          </Button>

                          {openMenu === menuKey && (
                            <Box
                              sx={{
                                display: 'flex',
                                backgroundColor: '#f5f5f5',
                                px: 2,
                                py: 1,
                                top: '64px',
                                position: 'fixed',
                                left: 0,
                                right: 0,
                                zIndex: 1,
                                width: '100vw',
                                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                                justifyContent: 'center',
                              }}
                            >
                              {menus[menuKey].map((item, idx) => {
                                const href = item.href.trim();
                                const isExternal = isExternalUrl(href);
                                const commonStyle = {
                                  padding: '8px 16px',
                                  textDecoration: 'none',
                                  color: '#000',
                                  fontSize: '14px',
                                  whiteSpace: 'nowrap',
                                  textAlign: 'center',
                                  borderLeft: idx !== 0 ? '1px solid #ddd' : 'none',
                                };

                                return isExternal ? (
                                  <a
                                    key={item.href}
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenu}
                                    style={commonStyle}
                                  >
                                    {item.name}
                                  </a>
                                ) : (
                                  <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={closeMenu}
                                    style={commonStyle}
                                  >
                                    {item.name}
                                  </Link>
                                );
                              })}

                            </Box>
                          )}
                        </Box>
                      </ClickAwayListener>
                    ))}
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
                {accessToken && clientConfig?.layout.callMe.active && showCallMeIcon && (
                  <IconButton onClick={handleOpenCallback}>
                    <Phone sx={{
                      fontSize: "2rem",
                      color: {
                        xs: "primary.contrastText",
                        md: clientConfig.layout.header.headerButtonColour,
                      }
                    }} />
                    {callbackSet && (
                      <AccessTimeIcon sx={{
                        fontSize: "1rem",
                        marginBottom: "15px",
                        marginLeft: "20px",
                        position: "absolute",
                        color: {
                          xs: "primary.contrastText",
                          md: clientConfig.layout.header.headerButtonColour,
                        }
                      }} />
                    )}
                  </IconButton>
                )}

                {accessToken && (
                    <IconButton
                      edge="end"
                      color="inherit"
                      onClick={toggleProfileDrawer(true)}
                      sx={{
                        fontSize: "2rem",
                        color: {
                          xs: "primary.contrastText",
                          md: clientConfig.layout.header.headerButtonColour,
                        },
                      }}
                    >
                      <PersonOutlineOutlinedIcon sx={{ fontSize: "2rem" }} />
                  </IconButton>

                )}

                {!accessToken && clientConfig?.layout.callMe.active && (
                  <Box sx={{ display: "flex", position: "relative", justifyContent: "flex-end", width: "100%" }}>
                    <IconButton onClick={handleOpenCallbackLogin} >
                      <Phone
                        sx={{
                          fontSize: "2rem",
                          color: {
                            xs: "primary.contrastText",
                            md: clientConfig.layout.header.headerButtonColour,
                          },
                        }}
                      />
                    </IconButton>
                    {callbackSet && (
                      <AccessTimeIcon
                        sx={{
                          fontSize: "1rem",
                          marginBottom: "15px",
                          marginLeft: "20px",
                          position: "absolute",
                          color: {
                            xs: "primary.contrastText",
                            md: clientConfig.layout.header.headerButtonColour,
                          },
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Toolbar>

          </Container>
        </AppBarStyle>
      </AppBar>

      {/* Mobile Burger Menu Drawer */}
      {!hideHeaderNavBars && <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          width: '100vw',
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: '100vw',
            backgroundColor: 'primary.dark',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          },
        }}
      >
        {/* Close Button at the Top Left */}
        {!hideHeaderNavBars && <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: '#fff', padding: '8px', mb: 1, ml: 2, mt: 1 }}>
            <CloseIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
        </Box>}

        {/* Menu Content Directly Below */}
        {!hideHeaderNavBars && <Box>
          {[
            ...(accessToken && showAccountMenu ? [{ label: "Manage my account", key: "account" }] : []),
            ...(showInfoMenu ? [{ label: "Info & Support", key: "info" }] : [])
          ].map(({ label, key }) => (
            <Accordion
              key={key}
              expanded={expandedSection === key}
              onChange={() => setExpandedSection(expandedSection === key ? null : key)}
              sx={{
                backgroundColor: 'primary.dark',
                boxShadow: 'none',
                borderRadius: 0,
                margin: 0,
                padding: 0,
                '&.Mui-expanded': { margin: 0 }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: '#fff' }} />}
                sx={{
                  color: '#fff',
                  minHeight: '48px',
                  height: '48px',
                  paddingLeft: '16px',
                  paddingRight: '16px'
                }}
              >
                <Typography>{label}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ backgroundColor: 'secondary.light', padding: 0, margin: 0 }}>
                {menus[key].map(item => {
                  const href = item.href.trim();
                  const isExternal = isExternalUrl(href);

                  const content = (
                    <Typography sx={{
                      p: 1,
                      pl: 2,
                      pr: 2,
                      borderBottom: '1px solid #fff',
                      color: '#000',
                      width: '100%'
                    }}>
                      {item.name}
                    </Typography>
                  );

                  return isExternal ? (
                    <a
                      key={href}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      style={{ textDecoration: 'none' }}
                    >
                      {content}
                    </a>
                  ) : (
                    <Link
                      key={href}
                      to={href}
                      onClick={() => setMobileMenuOpen(false)}
                      style={{ textDecoration: 'none' }}
                    >
                      {content}
                      </Link>
                  );
                })}

              </AccordionDetails>
            </Accordion>
          ))}
        </Box>}
      </Drawer>}

      {accessToken && clientConfig?.layout.callMe.active && <CallMeDialog open={openCallback} onClose={handleCloseCallback} onCallbackSet={handleCallbackSet} />}
      {!accessToken && clientConfig?.layout.callMe.active && <CallMeDialogLogin open={openCallDialogLogin} onClose={handleCloseCallbackLogin} onCallbackSet={handleCallbackSet} />}
      {accessToken && <Drawer anchor="right" open={profileDrawerOpen} onClose={() => setProfileDrawerOpen(false)} sx={{ '& .MuiDrawer-paper': { width: '85%', maxWidth: 450 } }}><Profile onClosePanel={() => setProfileDrawerOpen(false)} /></Drawer>}
      <Outlet />
      {showFooter && <Footer />}
    </>
  );
}

export default ResponsiveAppBar;
